import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { constants, price as priceUtil } from '@eti/utils';
import { PartOfExternalFlights } from '@eti/schema-types';
import { Card, Inline, Stack, Text, textVariants } from '@etg/wings';
import { ScreenReaderPrice } from '@eti/components';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../../../../utils/carriers';
import { transformPaymentTypes } from '../../../../../receipt/components/ReceiptPriceSpecification';
import {
  getAncillaryBundleSelectedOptionProducts,
  getExtraProducts,
  getTravelersDisplayPrices,
} from '../utils';
import { useOrderDetails } from '../../../../containers/OrderDetailsContext';
import { formatAmountInChargingCurrency, priceWithFullNameCurrency } from '../../../../utils';
import Travelers from './Travelers';
import PaymentItem from './PaymentItem';
import TravelersPerCarrier from './TravelersPerCarrier';
import AddOnOrders from './AddOnOrders';
import ExtraProducts from './ExtraProducts';

const inlineStyles = css`
  justify-content: space-between;
`;

const greyBackgroundStyles = css`
  background-color: #d6d6d6;
`;

const stackStyles = css`
  padding-inline-start: 8px;
`;

const maskedCardNumberStyles = css`
  padding-top: 4px;
`;

const footerNoteStyles = css`
  width: 70%;
`;

const PriceSpecification = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { language, siteCurrency } = useSiteContext();
  const {
    addOnOrders,
    ancillaryBundles,
    bounds,
    cc2carrierBreakdown,
    chargeCurrencyPriceFormat,
    discountPrice,
    instalmentsTotalFee,
    extraProducts,
    orderNumber,
    partOfExternalFlights,
    paymentTypes,
    purchaseDate,
    serviceFee,
    shouldAddInstallmentFeeInTotalAmount,
    totalPrice,
    totalPriceIncludingAddOnOrders,
    totalPriceIncludingAddOnOrdersInChargeCurrency,
    travelerPrices,
    travelers,
  } = useOrderDetails();

  const productsWithoutMarkup = p('IbeClient.PriceBreakdown.ProductsIdsToExcludeMarkup');
  const shouldShowTotalMarkup = partOfExternalFlights !== PartOfExternalFlights.None;
  const shouldShowCarrierPricesBreakdown = partOfExternalFlights === PartOfExternalFlights.Some;
  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    p('BookingFlow.SupplierCostMarkupBreakdown.Carriers'),
    p('BookingFlow.HideTaxIfZero.Carriers'),
  );
  const orderExtraProducts = getExtraProducts(
    extraProducts,
    shouldShowTotalMarkup,
    productsWithoutMarkup,
    marketingCarrierNames,
    shouldShowCarrierPricesBreakdown,
  );

  const displayPricesPerGroup = getTravelersDisplayPrices(
    travelers,
    travelerPrices,
    shouldShowTotalMarkup,
  );
  const paymentLabels = {
    paymentMethodBankText: t('Payment.Method.Bank.Text'),
    paymentMethodCreditCardText: t('Receipt.ReceiptDetails.Label.PaymentType.Card'),
    paymentMethodKlarnaPayLaterText: t('Payment.Method.Klarna.PayLater.Text'),
    paymentMethodKlarnaPayNowText: t('Payment.Method.Klarna.PayNow.Text'),
    paymentMethodKlarnaSliceItText: t('Payment.Method.Klarna.SliceIt.Text'),
    paymentMethodKlarnaSofortText: t('Payment.Method.Klarna.Sofortuberweisung.Text'),
    paymentMethodPayInternalText: t('Payment.Method.PayInternal.Text'),
  };

  const hasAddOnOrders = addOnOrders.length > 0;

  const sumLabel = hasAddOnOrders
    ? t('Order.Receipt.SubTotal.Header')
    : t('Receipt.ReceiptDetails.Label.Sum');

  const isCC2COrder = cc2carrierBreakdown && cc2carrierBreakdown.length > 0;

  const uniqueCarrierNames = isCC2COrder
    ? Array.from(new Set(cc2carrierBreakdown.map(({ carrierName }) => carrierName))).join(', ')
    : '';

  const isOtherChargeCurrency = chargeCurrencyPriceFormat.currencySymbol !== siteCurrency.code;

  const chargeCurrencyPriceFormatter =
    isOtherChargeCurrency && priceUtil.createPriceFormatter(chargeCurrencyPriceFormat);

  const discountPriceValue = discountPrice?.price.value;

  const totalPriceWithFees =
    shouldAddInstallmentFeeInTotalAmount && instalmentsTotalFee ? instalmentsTotalFee : totalPrice;

  return (
    <Card data-testid="price-specification">
      <Card.Section>
        <Inline className={inlineStyles}>
          <Stack spacing={0}>
            <Text
              data-testid="priceSpecification-orderNumber-label"
              variant={textVariants._14_BOLD}
            >
              {t('Receipt.ReceiptDetails.Label.OrderNumber')}
            </Text>
            <Text
              className={constants.MOUSE_FLOW_CLASS_NAMES}
              data-testid="priceSpecification-orderNumber"
            >
              {orderNumber}
            </Text>
          </Stack>
          <Stack spacing={0}>
            <Text
              data-testid="priceSpecification-orderCreationDate-label"
              variant={textVariants._14_BOLD}
            >
              {t('Receipt.ReceiptDetails.Label.OrderDate')}
            </Text>
            <Text
              className={constants.MOUSE_FLOW_CLASS_NAMES}
              data-testid="priceSpecification-orderCreationDate"
            >
              {purchaseDate}
            </Text>
          </Stack>
        </Inline>
      </Card.Section>
      {shouldShowCarrierPricesBreakdown ? (
        <TravelersPerCarrier travelerPrices={displayPricesPerGroup} />
      ) : (
        <Travelers
          marketingCarrierNames={marketingCarrierNames}
          shouldShowTotalMarkup={shouldShowTotalMarkup}
          travelersPrices={displayPricesPerGroup}
        />
      )}
      {ancillaryBundles.map((bundle) => {
        if (!bundle.selectedOption) {
          return null;
        }
        const { selectedOption: selectedOptionInfo, selectedOptionProducts } =
          getAncillaryBundleSelectedOptionProducts(bundle);

        return selectedOptionInfo ? (
          <PaymentItem
            dataTestId={`priceSpecification-productBundle-${selectedOptionInfo.name}`}
            key={selectedOptionInfo.id}
            price={priceUtil.formatPrice(selectedOptionInfo.price.price.value)}
            title={selectedOptionInfo.name}
          >
            {selectedOptionProducts &&
              selectedOptionProducts.map((product, index) => (
                <Stack
                  className={stackStyles}
                  data-testid={`priceSpecification-productBundle-${selectedOptionInfo.name}-product-${product}`}
                  key={`key-${index}`}
                  spacing={4}
                >
                  <Text>{product}</Text>
                </Stack>
              ))}
          </PaymentItem>
        ) : null;
      })}
      {orderExtraProducts &&
        orderExtraProducts.map((extraProduct, index) => (
          <ExtraProducts
            key={index}
            product={extraProduct}
            shouldShowCarrierPricesBreakdown={shouldShowCarrierPricesBreakdown}
          />
        ))}
      {shouldShowTotalMarkup && (
        <PaymentItem
          dataTestId="priceSpecification-serviceFee"
          price={priceUtil.formatPrice(serviceFee.value)}
          title={t('Cart.Row.Markup.Label')}
        />
      )}
      {transformPaymentTypes(paymentTypes, paymentLabels).map(
        ({ maskedCardNumber, price, title, type }) => (
          <PaymentItem
            className={constants.MOUSE_FLOW_CLASS_NAMES}
            dataTestId={`priceSpecification-paymentType-${type}`}
            key={type}
            price={price}
            textVariant={textVariants._14_BOLD}
            title={title}
          >
            {maskedCardNumber && <Text className={maskedCardNumberStyles}>{maskedCardNumber}</Text>}
          </PaymentItem>
        ),
      )}
      {isCC2COrder &&
        cc2carrierBreakdown.map(({ carrierName, transactionAmount }, index) => {
          if (!carrierName || !transactionAmount) {
            return null;
          }

          const priceInChargingCurrency = formatAmountInChargingCurrency(
            language.code,
            transactionAmount.price.value,
            transactionAmount.price.currency,
          );

          return (
            <PaymentItem
              accessiblePrice={
                siteCurrency.code === transactionAmount.price.currency.code
                  ? priceUtil.formatPrice(transactionAmount.price.value)
                  : priceWithFullNameCurrency(
                      priceInChargingCurrency,
                      language.code,
                      transactionAmount.price.currency,
                    )
              }
              dataTestId={`priceSpecification-cc2carrier-${index}-${carrierName}-transaction-entry`}
              key={`${carrierName}-${transactionAmount.price.value}`}
              price={
                siteCurrency.code === transactionAmount.price.currency.code
                  ? priceUtil.formatPrice(transactionAmount.price.value)
                  : priceInChargingCurrency
              }
              title={carrierName}
            />
          );
        })}
      {discountPriceValue && (
        <PaymentItem
          dataTestId="priceSpecification-discountPrice"
          price={priceUtil.formatPrice(discountPriceValue)}
          title={t('Receipt.Discount.Label')}
        />
      )}
      {instalmentsTotalFee > 0 && (
        <PaymentItem
          dataTestId="priceSpecification-instalmentsTotalFee"
          price={priceUtil.formatPrice(instalmentsTotalFee)}
          title={t('Payment.CardInstalments.PaymentPlan.InstalmentFee.Label')}
        />
      )}
      <PaymentItem
        dataTestId="priceSpecification-totalPrice"
        price={priceUtil.formatPrice(totalPriceWithFees)}
        textVariant={textVariants._14_BOLD}
        title={sumLabel}
      />
      {hasAddOnOrders && <AddOnOrders sumLabel={sumLabel} />}
      <Card.Section className={greyBackgroundStyles}>
        <Stack spacing={8}>
          <Inline className={inlineStyles} data-testid="priceSpecification-totalAmount-paid">
            <Text variant={textVariants._14_BOLD}>{t('Receipt.ReceiptDetails.Label.Paid')}</Text>
            <Text aria-hidden variant={textVariants._14_BOLD}>
              {priceUtil.formatPrice(totalPriceIncludingAddOnOrders)}
            </Text>
            <ScreenReaderPrice price={priceUtil.formatPrice(totalPriceIncludingAddOnOrders)} />
          </Inline>
          {isOtherChargeCurrency && (
            <Inline className={inlineStyles} data-testid="priceSpecification-totalInChargeCurrency">
              <Text variant={textVariants._14_BOLD}>
                {`${t('Receipt.ReceiptDetails.Label.TotalInChargeCurrency')}:`}
              </Text>
              <Text aria-hidden variant={textVariants._14_BOLD}>
                {chargeCurrencyPriceFormatter &&
                  chargeCurrencyPriceFormatter.formatPrice(
                    totalPriceIncludingAddOnOrdersInChargeCurrency,
                  )}
              </Text>
              <ScreenReaderPrice
                price={
                  chargeCurrencyPriceFormatter
                    ? chargeCurrencyPriceFormatter.formatPrice(
                        totalPriceIncludingAddOnOrdersInChargeCurrency,
                      )
                    : ''
                }
              />
            </Inline>
          )}
          <Text asHtml className={footerNoteStyles}>
            {isCC2COrder
              ? t('Receipt.ReceiptDetails.CC2C.Text.Payee', uniqueCarrierNames)
              : t('Receipt.ReceiptDetails.Text.Payee')}
          </Text>
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default PriceSpecification;
